import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { useDebounced } from '@/components/parameters/useDebounced';
import type { HeadOfficeMatch } from '@/types';

/** A head office chosen from Profiles, exactly as Profiles spells it. */
export type ChosenHeadOffice = { headOfficeId: number; name: string };

/**
 * Pick one head office from Profiles.
 *
 * Lives here rather than in the apply wizard because the pack EDITOR needs the same control for a
 * different reason: a parameter whose valid values are resolved from a dataset taking the head
 * office as an argument has no list at all until one is named, so the author picks a specimen
 * customer purely to make the report answer. Both callers need the same three-way reading of a
 * search — results, no results, and "the directory could not be asked" — and a second copy of that
 * is how one of them quietly starts rendering an outage as "no such customer".
 *
 * No free text anywhere: the id decides the pickup folder and what a bound report is filtered to,
 * so it comes verbatim from a chosen Profiles row or not at all.
 */
export function HeadOfficePicker({
  onChoose,
  placeholder = 'Search head offices...',
  maxWidth = 520
}: {
  onChoose: (customer: ChosenHeadOffice) => void;
  placeholder?: string;
  maxWidth?: number;
}) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search.trim(), 250);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['pack-head-office-search', debounced],
    queryFn: () => api.packHeadOfficeSearch(debounced),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
    // A failed search should say so immediately rather than spend several seconds retrying with
    // backoff before admitting it could not check — same as every other live-search picker here.
    retry: false
  });

  const matches = data?.matches ?? [];
  // A request that threw and a response that came back saying `available: false` mean the same
  // thing here: the directory could not be ASKED. Rendering either as "no results" would read as
  // "this customer does not exist", which is a different and much more misleading claim.
  const unavailable = isError || data?.available === false;

  return (
    <>
      <input
        className="input"
        style={{ width: '100%', maxWidth }}
        type="search"
        placeholder={placeholder}
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoComplete="off"
      />

      {debounced.length < 2 ? (
        <div className="field-hint">Type at least two characters of the customer's name.</div>
      ) : (
        <div style={{
          marginTop: 8,
          maxWidth,
          border: '1px solid var(--kt-grey-200)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden'
        }}>
          {isFetching ? (
            <div className="placeholder" style={{ padding: 14 }}>Searching...</div>
          ) : unavailable ? (
            <div className="notice notice-warn" style={{ margin: 0, borderRadius: 0, border: 'none' }}>
              <strong>The customer directory could not be reached.</strong>
              <div>
                This is not a result — it means the search itself could not be run, so nothing is
                known about whether “{debounced}” exists. Try again shortly.
              </div>
            </div>
          ) : matches.length === 0 ? (
            <div className="placeholder" style={{ padding: 14 }}>
              No head office matches “{debounced}”.
            </div>
          ) : matches.map((match: HeadOfficeMatch) => (
            <button
              key={match.headOfficeId}
              type="button"
              onClick={() => onChoose({ headOfficeId: match.headOfficeId, name: match.name })}
              style={{
                display: 'flex',
                gap: 12,
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                border: 'none',
                borderBottom: '1px solid var(--kt-grey-100)',
                background: 'transparent',
                font: 'inherit',
                color: 'inherit'
              }}>
              <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', color: 'var(--kt-grey-500)', fontSize: '.84rem' }}>
                {match.headOfficeId}
              </span>
              <span>{match.name}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
